import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { X, Phone, Mail, MapPin, Users, Calendar, Filter, RefreshCw } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const beeIcon = new L.DivIcon({
  className: 'custom-bee-marker',
  html: '<div style="background: #fbbf24; width: 30px; height: 30px; border-radius: 50%; border: 3px solid #f59e0b; display: flex; align-items: center; justify-content: center; font-size: 16px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">🐝</div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

const swarmIcon = new L.DivIcon({
  className: 'custom-swarm-marker',
  html: '<div style="background: #f97316; width: 36px; height: 36px; border-radius: 50%; border: 3px solid #ea580c; display: flex; align-items: center; justify-content: center; font-size: 20px; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">🐝</div>',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

const BeekeeperMap = ({ onClose, embedded = false }) => {
  const [beekeepers, setBeekeepers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState([-37.8136, 144.9631]); // Default: Melbourne
  const [filterSwarmRescue, setFilterSwarmRescue] = useState(false);
  const [filterMentor, setFilterMentor] = useState(false);
  const [filterBeeClub, setFilterBeeClub] = useState(false);
  const [filterSupplies, setFilterSupplies] = useState(false);
  const [filterQueenSupplier, setFilterQueenSupplier] = useState(false);
  
  // Activity filters - dynamic
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityFilters, setActivityFilters] = useState({});
  
  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showActivityFilters, setShowActivityFilters] = useState(false);

  // Fetch activity types
  useEffect(() => {
    const fetchActivityTypes = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API}/api/config/happening_now`, { headers });
        const data = await res.json();
        const types = (data.items || []).filter(t => t.map_visible !== false && t.is_active !== false);
        setActivityTypes(types);
        
        // Initialize filters
        const initialFilters = {};
        types.forEach(t => {
          const key = t.field_name || t.name.toLowerCase().replace(/\s+/g, '_');
          initialFilters[key] = false;
        });
        setActivityFilters(initialFilters);
      } catch (err) {
        // Fallback
        setActivityTypes([
          { name: 'Harvesting', icon: '🍯', color: 'amber', field_name: 'currently_harvesting' },
          { name: 'Feeding', icon: '🍽️', color: 'blue', field_name: 'currently_feeding' },
          { name: 'Mites Alert', icon: '🐛', color: 'red', field_name: 'mites_detected' },
        ]);
        setActivityFilters({ currently_harvesting: false, currently_feeding: false, mites_detected: false });
      }
    };
    fetchActivityTypes();
  }, []);

  useEffect(() => {
    fetchBeekeepers();
    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          // Use default if geolocation fails
        }
      );
    }
  }, []);

  // Fetch when activity filters change
  useEffect(() => {
    const hasActiveFilters = Object.values(activityFilters).some(v => v) || startDate || endDate;
    if (hasActiveFilters) {
      fetchActiveBeekeepers();
    } else {
      fetchBeekeepers();
    }
  }, [activityFilters, startDate, endDate]);

  const fetchBeekeepers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/public/beekeepers`);
      if (response.ok) {
        const data = await response.json();
        setBeekeepers(data);
        // Center on first beekeeper if available
        if (data.length > 0 && data[0].gps_lat && data[0].gps_lng) {
          setCenter([data[0].gps_lat, data[0].gps_lng]);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveBeekeepers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Add dynamic activity filters
      Object.entries(activityFilters).forEach(([key, value]) => {
        if (value) params.append(key, 'true');
      });
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/api/beekeepers/active?${params.toString()}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setBeekeepers(data);
        if (data.length > 0 && data[0].gps_lat && data[0].gps_lng) {
          setCenter([data[0].gps_lat, data[0].gps_lng]);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const clearActivityFilters = () => {
    setActivityFilters(prev => {
      const cleared = {};
      Object.keys(prev).forEach(k => cleared[k] = false);
      return cleared;
    });
    setStartDate('');
    setEndDate('');
  };

  const hasActivityFilters = Object.values(activityFilters).some(v => v) || startDate || endDate;

  const filteredBeekeepers = beekeepers.filter(bk => {
    if (filterSwarmRescue && !bk.offers_swarm_rescue) return false;
    if (filterMentor && !bk.offers_mentoring) return false;
    if (filterBeeClub && !bk.is_bee_club) return false;
    if (filterSupplies && !bk.is_beekeeping_supplies) return false;
    if (filterQueenSupplier && !bk.is_queen_supplier) return false;
    return true;
  });

  return (
    <div className={embedded ? "" : "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4"}>
      <div className={embedded ? "flex flex-col" : "bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[95vh] md:max-h-[90vh] flex flex-col overflow-hidden"}>
        {/* Header */}
        <div className="p-3 md:p-4 border-b border-stone-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Users className="h-6 w-6 text-amber-600" />
              Find Beekeepers
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              {filteredBeekeepers.length} beekeeper{filteredBeekeepers.length !== 1 ? 's' : ''} found
            </p>
          </div>
          {!embedded && (
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors z-[1001] bg-white/80"
          >
            <X className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          )}
        </div>

        {/* Filters - Unified Section */}
        <div className="p-3 bg-stone-50 border-b border-stone-200 space-y-3 flex-shrink-0 overflow-y-auto max-h-[50vh] md:max-h-none md:overflow-visible">
          {/* Show All Beekeepers Toggle */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer bg-amber-100 px-3 py-2 rounded-lg border-2 border-amber-400">
              <input
                type="checkbox"
                checked={!filterSwarmRescue && !filterMentor && !filterSupplies && !filterBeeClub && !filterQueenSupplier && !hasActivityFilters}
                onChange={() => {
                  setFilterSwarmRescue(false);
                  setFilterMentor(false);
                  setFilterSupplies(false);
                  setFilterBeeClub(false);
                  setFilterQueenSupplier(false);
                  clearActivityFilters();
                }}
                className="w-4 h-4 rounded border-amber-400 text-amber-600"
              />
              <span className="font-semibold text-amber-800">🐝 All Beekeepers</span>
            </label>
            <span className="text-xs text-stone-500">or select specific filters below:</span>
          </div>

          {/* Services Section */}
          <div>
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Users className="w-3 h-3" />
              Filter by Services Offered
            </div>
            <div className="flex flex-wrap gap-2">
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${filterSwarmRescue ? 'bg-orange-100 border-orange-400 ring-2 ring-orange-400' : 'bg-white border-stone-200 hover:border-orange-300'}`}>
                <input
                  type="checkbox"
                  checked={filterSwarmRescue}
                  onChange={e => setFilterSwarmRescue(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-stone-300 text-orange-600"
                />
                <span className="font-medium text-stone-700">🐝 Swarm Rescue</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${filterMentor ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-400' : 'bg-white border-stone-200 hover:border-blue-300'}`}>
                <input
                  type="checkbox"
                  checked={filterMentor}
                  onChange={e => setFilterMentor(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-stone-300 text-blue-600"
                />
                <span className="font-medium text-stone-700">📚 Mentor</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${filterSupplies ? 'bg-purple-100 border-purple-400 ring-2 ring-purple-400' : 'bg-white border-stone-200 hover:border-purple-300'}`}>
                <input
                  type="checkbox"
                  checked={filterSupplies}
                  onChange={e => setFilterSupplies(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-stone-300 text-purple-600"
                />
                <span className="font-medium text-stone-700">🛒 Supplies</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${filterBeeClub ? 'bg-pink-100 border-pink-400 ring-2 ring-pink-400' : 'bg-white border-stone-200 hover:border-pink-300'}`}>
                <input
                  type="checkbox"
                  checked={filterBeeClub}
                  onChange={e => setFilterBeeClub(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-stone-300 text-pink-600"
                />
                <span className="font-medium text-stone-700">🏛️ Bee Club</span>
              </label>
              <label className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${filterQueenSupplier ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-400' : 'bg-white border-stone-200 hover:border-amber-300'}`}>
                <input
                  type="checkbox"
                  checked={filterQueenSupplier}
                  onChange={e => setFilterQueenSupplier(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-stone-300 text-amber-600"
                />
                <span className="font-medium text-stone-700">👑 Queens</span>
              </label>
            </div>
          </div>

          {/* Activities Section - Collapsible */}
          <div>
            <button
              onClick={() => setShowActivityFilters(!showActivityFilters)}
              className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2 hover:text-stone-700"
            >
              <Filter className="w-3 h-3" />
              Filter by Current Activity ("Happening Now")
              <span className={`transform transition-transform ${showActivityFilters ? 'rotate-180' : ''}`}>▼</span>
              {hasActivityFilters && <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full normal-case">Active</span>}
            </button>
            
            {showActivityFilters && (
              <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-700 mb-2">
                  💡 Only shows beekeepers who have this activity ACTIVE right now
                </p>
                <div className="flex flex-wrap gap-2">
                  {activityTypes.map(activity => {
                    const key = activity.field_name || activity.name.toLowerCase().replace(/\s+/g, '_');
                    return (
                      <label 
                        key={key} 
                        className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-lg border text-xs sm:text-sm ${
                          activityFilters[key] 
                            ? 'bg-amber-500 border-amber-600 text-white ring-2 ring-amber-600' 
                            : 'bg-white border-stone-200 hover:border-amber-300 text-stone-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={activityFilters[key] || false}
                          onChange={e => setActivityFilters(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="sr-only"
                        />
                        <span className="font-medium">{activity.icon} {activity.name}</span>
                      </label>
                    );
                  })}
                </div>
                
                {hasActivityFilters && (
                  <button 
                    onClick={clearActivityFilters}
                    className="mt-2 text-xs text-amber-700 hover:text-amber-900 underline flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" /> Clear activity filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="px-3 py-2 bg-stone-100 border-b border-stone-200 flex-shrink-0">
          <div className="flex flex-wrap gap-3 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded-full bg-amber-400 border-2 border-amber-500 inline-flex items-center justify-center text-[10px]">🐝</span>
              Beekeeper
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded-full bg-orange-500 border-2 border-orange-600 inline-flex items-center justify-center text-[10px]">🐝</span>
              Swarm Rescue
            </span>
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 min-h-[300px]">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl mb-2">🗺️</div>
                <p className="text-stone-500">Loading map...</p>
              </div>
            </div>
          ) : (
            <div className="relative h-full">
              <style>{`
                .leaflet-control-container { z-index: 400 !important; }
                .leaflet-top, .leaflet-bottom { z-index: 400 !important; }
                .leaflet-control-zoom { z-index: 400 !important; }
                .leaflet-control-zoom a { 
                  z-index: 400 !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                }
              `}</style>
              <MapContainer
                key={`${center[0]}-${center[1]}-${filteredBeekeepers.length}`}
                center={center}
                zoom={10}
                style={{ height: '100%', width: '100%', minHeight: '300px' }}
                scrollWheelZoom={true}
              >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {filteredBeekeepers.map((bk) => (
                <Marker
                  key={bk.id}
                  position={[bk.gps_lat, bk.gps_lng]}
                  icon={bk.offers_swarm_rescue ? swarmIcon : beeIcon}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      {bk.location_approximate && (
                        <div className="mb-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                          📍 Approximate location (~10km radius)
                        </div>
                      )}
                      {bk.name && (
                        <h3 className="font-bold text-stone-800 text-lg">{bk.name}</h3>
                      )}
                      {bk.company_name && (
                        <p className="text-sm text-amber-600 font-medium">{bk.company_name}</p>
                      )}
                      
                      {(bk.city || bk.state) && (
                        <p className="text-sm text-stone-500 flex items-center gap-1 mt-2">
                          <MapPin className="w-3 h-3" />
                          {[bk.city, bk.state, bk.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                      
                      {bk.phone && (
                        <p className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          <a href={`tel:${bk.phone}`} className="text-blue-600 hover:underline">{bk.phone}</a>
                        </p>
                      )}
                      
                      {bk.email && (
                        <p className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" />
                          <a href={`mailto:${bk.email}`} className="text-blue-600 hover:underline">{bk.email}</a>
                        </p>
                      )}
                      
                      {/* Current Activities - Dynamic */}
                      {(() => {
                        const activeActivities = activityTypes.filter(activity => {
                          const key = activity.field_name || activity.name.toLowerCase().replace(/\s+/g, '_');
                          // Check both camelCase and snake_case versions
                          const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
                          return bk[key] || bk[camelKey] || bk[`currently_${key}`];
                        });
                        
                        if (activeActivities.length === 0) return null;
                        
                        const colorClasses = {
                          amber: 'bg-amber-100 text-amber-700',
                          blue: 'bg-blue-100 text-blue-700',
                          red: 'bg-red-100 text-red-700',
                          green: 'bg-green-100 text-green-700',
                          purple: 'bg-purple-100 text-purple-700',
                          pink: 'bg-pink-100 text-pink-700',
                          orange: 'bg-orange-100 text-orange-700',
                          teal: 'bg-teal-100 text-teal-700',
                        };
                        
                        return (
                          <div className="mt-3 pt-2 border-t border-stone-100">
                            <p className="text-xs text-stone-500 mb-1">🔔 Happening Now:</p>
                            <div className="flex flex-wrap gap-1">
                              {activeActivities.map(activity => (
                                <span 
                                  key={activity.field_name || activity.name} 
                                  className={`px-2 py-0.5 text-xs rounded-full ${colorClasses[activity.color] || 'bg-stone-100 text-stone-700'}`}
                                >
                                  {activity.icon} {activity.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      
                      <div className="flex flex-wrap gap-1 mt-3">
                        {bk.offers_swarm_rescue && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                            🐝 Swarm Rescue
                          </span>
                        )}
                        {bk.offers_mentoring && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            📚 Mentor
                          </span>
                        )}
                        {bk.is_bee_club && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                            🏛️ Bee Club
                          </span>
                        )}
                        {bk.is_beekeeping_supplies && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                            🛒 Supplies
                          </span>
                        )}
                        {bk.is_queen_supplier && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                            👑 Queen Supplier
                          </span>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
              </MapContainer>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default BeekeeperMap;
